import React, { Suspense, lazy, useState, useEffect, useRef } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from './hooks/useAuth';
import { useBranding } from './hooks/useBranding';
import { useBuildingDisplayDefaults } from './hooks/useBuildingDisplayDefaults';
import { useSip } from './hooks/useSip';
import { useAutoProvision } from './hooks/useAutoProvision';
import { useDeviceTokenParam } from './hooks/useDeviceTokenParam';
import { useKioskMode } from './hooks/useKioskMode';
import { useReportPermissions } from './hooks/useReportPermissions';
import { measureAudioReadiness, formatAudioReadiness } from './utils/audioReadiness';
import { LoginPage } from './pages/LoginPage';
import { HomePage } from './pages/HomePage';
import { EmergencyPage } from './pages/EmergencyPage';
import { ScreensaverController } from './components/ScreensaverController';
import { DimOverlay } from './components/DimOverlay';
import { SleepOverlay } from './components/SleepOverlay';
import { EmergencyButton } from './components/EmergencyButton';
import { PermissionGate } from './components/PermissionGate';
import { BatteryWarning } from './components/BatteryWarning';
import { WebSplash } from './components/WebSplash';
import { DebugOverlay } from './components/DebugOverlay';
import { useMqttTelemetry } from './hooks/useMqttTelemetry';
import { startConnectivityHeartbeat, stopConnectivityHeartbeat } from './services/connectivityHeartbeat';
import { CommandOverlay } from './components/CommandOverlay';
import { NotificationDialog } from './components/NotificationDialog';
import { NotificationFullPage } from './components/NotificationFullPage';
import { useNotificationTransport } from './hooks/useResidentNotifications';
import { BuildingFeaturesProvider } from './components/BuildingFeaturesProvider';
import { FeatureGate } from '@caro/shared/features';
import type { FeatureKey } from '@caro/shared/features';
import './styles/global.css';
import { Z_INDEX } from './constants/zIndex';

// Lazy-load pages that pull in heavy deps (sip.js ~150KB, camera APIs)
const ConnectivityPage = lazy(() => import('./pages/ConnectivityPage').then(m => ({ default: m.ConnectivityPage })));
const QRLoginPage = lazy(() => import('./pages/QRLoginPage').then(m => ({ default: m.QRLoginPage })));
const AdminSetupPage = lazy(() => import('./pages/AdminSetupPage').then(m => ({ default: m.AdminSetupPage })));
const InCallPage = lazy(() => import('./pages/InCallPage').then(m => ({ default: m.InCallPage })));
const IncomingCallPage = lazy(() => import('./pages/IncomingCallPage').then(m => ({ default: m.IncomingCallPage })));
const DoorbellPage = lazy(() => import('./pages/DoorbellPage').then(m => ({ default: m.DoorbellPage })));
const SettingsPage = lazy(() => import('./pages/SettingsPage').then(m => ({ default: m.SettingsPage })));
const TestModePage = lazy(() => import('./pages/TestModePage').then(m => ({ default: m.TestModePage })));
const ContactsPage = lazy(() => import('./pages/ContactsPage').then(m => ({ default: m.ContactsPage })));
const PhotosPage = lazy(() => import('./pages/PhotosPage').then(m => ({ default: m.PhotosPage })));
const InfoPage = lazy(() => import('./pages/InfoPage').then(m => ({ default: m.InfoPage })));
const VoicemailPage = lazy(() => import('./pages/VoicemailPage').then(m => ({ default: m.VoicemailPage })));
const CallHistoryPage = lazy(() => import('./pages/CallHistoryPage').then(m => ({ default: m.CallHistoryPage })));
const HomeScreenSettingsPage = lazy(() => import('./pages/HomeScreenSettingsPage').then(m => ({ default: m.HomeScreenSettingsPage })));
const DialerPage = lazy(() => import('./pages/DialerPage').then(m => ({ default: m.DialerPage })));
const AppsPage = lazy(() => import('./pages/AppsPage').then(m => ({ default: m.AppsPage })));
const GamesPage = lazy(() => import('./pages/GamesPage').then(m => ({ default: m.GamesPage })));
const MemoryGamePage = lazy(() => import('./pages/games/MemoryGamePage').then(m => ({ default: m.MemoryGamePage })));
const SudokuPage = lazy(() => import('./pages/games/SudokuPage').then(m => ({ default: m.SudokuPage })));
const WordSearchPage = lazy(() => import('./pages/games/WordSearchPage').then(m => ({ default: m.WordSearchPage })));
const TriviaPage = lazy(() => import('./pages/games/TriviaPage').then(m => ({ default: m.TriviaPage })));
const Game2048Page = lazy(() => import('./pages/games/Game2048Page').then(m => ({ default: m.Game2048Page })));
const SolitairePage = lazy(() => import('./pages/games/SolitairePage').then(m => ({ default: m.SolitairePage })));
const TicTacToePage = lazy(() => import('./pages/games/TicTacToePage').then(m => ({ default: m.TicTacToePage })));
const HangmanPage = lazy(() => import('./pages/games/HangmanPage').then(m => ({ default: m.HangmanPage })));
const SimonPage = lazy(() => import('./pages/games/SimonPage').then(m => ({ default: m.SimonPage })));
const ChessPage = lazy(() => import('./pages/games/ChessPage').then(m => ({ default: m.ChessPage })));
const CheckersPage = lazy(() => import('./pages/games/CheckersPage').then(m => ({ default: m.CheckersPage })));
const FindDifferencesPage = lazy(() => import('./pages/games/FindDifferencesPage').then(m => ({ default: m.FindDifferencesPage })));
const SlidingPuzzlePage = lazy(() => import('./pages/games/SlidingPuzzlePage').then(m => ({ default: m.SlidingPuzzlePage })));
const AgendaPage = lazy(() => import('./pages/AgendaPage').then(m => ({ default: m.AgendaPage })));
const DomoticsPage = lazy(() => import('./pages/DomoticsPage'));
const DisplaySettingsPage = lazy(() => import('./pages/DisplaySettingsPage').then(m => ({ default: m.DisplaySettingsPage })));
const PermissionsSettingsPage = lazy(() => import('./pages/PermissionsSettingsPage').then(m => ({ default: m.PermissionsSettingsPage })));
const InAppBrowserPage = lazy(() => import('./pages/InAppBrowserPage').then(m => ({ default: m.InAppBrowserPage })));
const TodayPage = lazy(() => import('./pages/TodayPage').then(m => ({ default: m.TodayPage })));
const WeatherPage = lazy(() => import('./pages/WeatherPage').then(m => ({ default: m.WeatherPage })));
const NewsPage = lazy(() => import('./pages/NewsPage').then(m => ({ default: m.NewsPage })));
const NotificationsPage = lazy(() => import('./pages/NotificationsPage').then(m => ({ default: m.NotificationsPage })));

/**
 * Watches for incoming SIP calls and navigates to /incoming automatically.
 * Must be rendered inside BrowserRouter.
 */
function CallRouter() {
  const { currentCall } = useSip();
  const navigate = useNavigate();
  const location = useLocation();
  const prevCallRef = useRef<string | null>(null);

  useEffect(() => {
    if (!currentCall) {
      prevCallRef.current = null;
      return;
    }

    // Only auto-navigate for NEW incoming calls (not our own outgoing calls)
    if (
      currentCall.direction === 'inbound' &&
      currentCall.state === 'ringing_in' &&
      prevCallRef.current !== currentCall.id &&
      location.pathname !== '/incoming' &&
      location.pathname !== '/doorbell' &&
      // The emergency screen handles its own call-back from emergency services:
      // it auto-answers and turns itself into the call screen (timer + "in
      // gesprek", no hangup button). Don't hijack it to /incoming or /call —
      // those offer a hangup and would drop the resident out of alarm mode.
      location.pathname !== '/emergency'
    ) {
      prevCallRef.current = currentCall.id;
      const ext = currentCall.remoteParty;

      // Fast path: 4-digit videophone extensions (7XXX) → doorbell immediately.
      if (/^7\d{3}$/.test(ext)) {
        navigate('/doorbell');
        return;
      }

      // Everything else lands on the generic incoming screen. IncomingCallPage
      // then resolves the caller via DEVICE_BY_EXTENSION and hands camera /
      // videophone callers off to /doorbell (works for non-7XXX extensions too).
      navigate('/incoming');
    }
  }, [currentCall, navigate, location.pathname]);

  return null;
}

/**
 * Zero-touch auto-provisioning boot hook (R1). Rendered inside the Router when
 * the device is unauthenticated: on cold start with no token it reads the
 * Hexnode UDID from the managed-launch URL (`?hexnodeUDID=<udid>`) and attempts
 * auto-provision, landing on `/` (success) or `/admin-setup` (fail-closed PIN
 * fallback). Runs before the `/login` redirect.
 */
/**
 * Reports this device's OS permission status to the backend on change, feeding
 * the portal's tablet-permissions overview. Mounted only when authenticated.
 */
function PermissionReporter() {
  useReportPermissions();
  return null;
}

/**
 * Measure whether this device can make a sound BEFORE anyone touches it.
 * Must run at start-up: the first touch unlocks audio, so any later check
 * would always report "fine" — including on a device that was silent all night.
 * Result is read back on the info screen.
 */
function AudioReadinessBoot() {
  useEffect(() => {
    void measureAudioReadiness().then((r) => {
      console.info('[audio-readiness]', formatAudioReadiness(r), r);
    });
  }, []);
  return null;
}

/**
 * De weg TERUG naar de server voor bewonersmeldingen.
 *
 * Stond tot 2026-09-09 in `useResidentNotifications()`, die alleen door `NotificationsPage`
 * gemount wordt. Een melding komt binnen via MQTT en heeft die pagina niet nodig, dus op een
 * tablet op het startscherm verscheen de melding wel en kon er niets terug: de aflevering
 * bleef op `pushed` en elke actieknop antwoordde "Geen verbinding". Zie de kop van
 * `useNotificationTransport`.
 */
function NotificationTransportBoot() {
  useNotificationTransport();
  return null;
}

function AutoProvisionBoot() {
  // Standalone-hub kiosk: ingest a hub-minted `?deviceToken=` FIRST so it wins
  // when both params are somehow present (the hub already minted the token, so
  // no provisioning round-trip is needed). Then the Hexnode UDID auto-provision
  // flow runs; with a token already stored it no-ops / refreshes safely.
  useKioskMode();
  useDeviceTokenParam();
  useAutoProvision();
  return null;
}

/**
 * Global emergency button — shows on all authenticated pages.
 * Full bar on home page, compact icon on other pages.
 * Hidden on emergency page itself, login, and call pages.
 */
function GlobalEmergencyButton() {
  const navigate = useNavigate();
  const location = useLocation();
  // '/' is hidden because HomePage renders its own large in-page emergency
  // button (the redesigned right-column control); the global floating button
  // would duplicate it.
  const hiddenPaths = ['/', '/emergency', '/login', '/qr-login', '/admin-setup', '/call', '/incoming', '/doorbell'];
  if (hiddenPaths.includes(location.pathname)) return null;
  return <EmergencyButton onClick={() => navigate('/emergency')} />;
}

/** Activates MQTT telemetry + shows session conflict overlay */
function MqttTelemetryProvider() {
  const { sessionConflict } = useMqttTelemetry();

  // Connectivity heartbeat: 60s local probes → buffered → flushed to the API on
  // change or every 15 min. Mounted here because this provider is already gated
  // on isAuthenticated, so start/stop track the authenticated session.
  useEffect(() => {
    startConnectivityHeartbeat();
    return () => stopConnectivityHeartbeat();
  }, []);

  if (sessionConflict) {
    return (
      <div style={{
        position: 'fixed',
        inset: 0,
        zIndex: Z_INDEX.SESSION_CONFLICT,
        backgroundColor: 'rgba(0,0,0,0.85)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
      }}>
        <div style={{
          backgroundColor: '#fff',
          borderRadius: 16,
          padding: '32px 24px',
          maxWidth: 400,
          textAlign: 'center',
          boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
        }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>&#128274;</div>
          <h2 style={{ margin: '0 0 12px', fontSize: 20, color: '#dc2626' }}>
            Session Ended
          </h2>
          <p style={{ margin: 0, fontSize: 16, color: '#555', lineHeight: 1.5 }}>
            You have been logged out because the same account was used to sign in on another device.
            SIP can only be active on one device at a time.
          </p>
          <p style={{ margin: '16px 0 0', fontSize: 14, color: '#999' }}>
            Redirecting to login...
          </p>
        </div>
      </div>
    );
  }

  return null;
}

/**
 * Wrap a feature-bearing route element in a `<FeatureGate>` (R2 / T034).
 * When the building has the feature toggled off, the route redirects Home
 * instead of rendering. `emergency` is never gated (enforced by FeatureGate).
 */
function gatedRoute(feature: FeatureKey, element: React.ReactNode): React.ReactElement {
  return (
    <FeatureGate feature={feature} fallback={<Navigate to="/" replace />}>
      {element}
    </FeatureGate>
  );
}

function PageLoader() {
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100vh',
      fontSize: 'var(--font-body)',
      color: '#888',
    }}>Loading...
          </div>
  );
}

export function App() {
  const { user, isAuthenticated, isLoading } = useAuth();
  const [splashDone, setSplashDone] = useState(false);

  // Apply building branding at the App root (T1.3 / Phase 1). Previously this
  // lived in HomePage and stripped CSS custom properties on unmount, which
  // caused branding to evaporate whenever the user navigated away from Home.
  // Hoisting it here makes the bridge run exactly once for the lifetime of
  // the React tree. Safe to call with undefined when unauthenticated — the
  // hook short-circuits via useApi(null).
  useBranding(user?.tenantId);

  // Apply per-location display defaults (theme / clock / font / screensaver
  // allowed) from the portal on launch + when the building config changes.
  // Live edits also arrive via the `display_settings` MQTT/WS command.
  useBuildingDisplayDefaults(user?.tenantId);

  // Gate 1: Web splash (always shows briefly on cold start)
  if (!splashDone) {
    return <WebSplash onComplete={() => setSplashDone(true)} />;
  }

  if (isLoading) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        backgroundColor: '#f5f5f5',
        fontSize: 'var(--font-heading)',
        color: '#666',
      }}>Loading...
              </div>
    );
  }

  // R2 / T034: when authenticated, seed the shared FeatureProvider from the
  // building's merged effective flags so route/widget gating reflects the
  // per-building config. Unauthenticated boot (login / auto-provision) renders
  // without the provider — there is no tenant context yet.
  const Shell = isAuthenticated ? BuildingFeaturesProvider : React.Fragment;

  return (
    <PermissionGate>
    <BrowserRouter>
      <Shell>
      {/* Always mounted: cold-start provisioning when unauthenticated, AND a
          once-per-load profile refresh when already authenticated (picks up
          language/building/extension changes without a manual storage clear). */}
      <AutoProvisionBoot />
      <AudioReadinessBoot />
      {isAuthenticated && <CallRouter />}
      {isAuthenticated && <PermissionReporter />}
      {isAuthenticated && <GlobalEmergencyButton />}
      {isAuthenticated && <BatteryWarning />}
      {isAuthenticated && <MqttTelemetryProvider />}
      {isAuthenticated && <CommandOverlay />}
      {/* Vóór de dialoog en de fullpage: die twee acken zodra ze geschilderd hebben, en een
          ack zonder transport is stil verloren. */}
      {isAuthenticated && <NotificationTransportBoot />}
      {/* Bewonersmelding als dialoog (T-2.05). Staat op 9950 en dus boven het
          `show_message`-vangnet (9940) en boven de stand-bylaag, maar onder de noodmelding
          (9975) en een oproep (9990) — een melding gaat nooit boven wie belt. */}
      {isAuthenticated && <NotificationDialog />}
      {/* Bewonersmelding over het volledige scherm (T-3.01). Bewust een OVERLAY en geen
          route: een route vervuilt de navigatiestack en laat de tablet na een crash of een
          WebView-herstart op een meldingspagina staan van een melding die allang weg is.
          Ligt op 9960, dus boven de dialoog (9950) en onder de noodmelding (9975). */}
      {isAuthenticated && <NotificationFullPage />}
      {isAuthenticated && <ScreensaverController />}
      {isAuthenticated && <DimOverlay />}
      {isAuthenticated && <SleepOverlay />}
      {/* Always mounted (not auth-gated): reachable on /login, /admin-setup and
          during a provisioning loop so we can diagnose on locked kiosk tablets. */}
      <DebugOverlay />
      <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* Public routes (no auth required) */}
          <Route
            path="/login"
            element={isAuthenticated ? <Navigate to="/" replace /> : <LoginPage />}
          />
          <Route path="/qr-login" element={<QRLoginPage />} />
          <Route path="/admin-setup" element={<AdminSetupPage />} />

          {/* Protected routes */}
          <Route
            path="/"
            element={isAuthenticated ? <HomePage /> : <Navigate to="/login" replace />}
          />
          <Route
            path="/emergency"
            element={isAuthenticated ? <EmergencyPage /> : <Navigate to="/login" replace />}
          />
          <Route
            path="/connectivity"
            element={isAuthenticated ? <ConnectivityPage /> : <Navigate to="/login" replace />}
          />
          <Route
            path="/call"
            element={isAuthenticated ? <InCallPage /> : <Navigate to="/login" replace />}
          />
          <Route
            path="/incoming"
            element={isAuthenticated ? <IncomingCallPage /> : <Navigate to="/login" replace />}
          />
          <Route
            path="/doorbell"
            element={isAuthenticated ? <DoorbellPage /> : <Navigate to="/login" replace />}
          />
          <Route
            path="/settings"
            element={isAuthenticated ? <SettingsPage /> : <Navigate to="/login" replace />}
          />
          <Route
            path="/settings/home-screen"
            element={isAuthenticated ? <HomeScreenSettingsPage /> : <Navigate to="/login" replace />}
          />
          <Route
            path="/settings/display"
            element={isAuthenticated ? <DisplaySettingsPage /> : <Navigate to="/login" replace />}
          />
          <Route
            path="/settings/permissions"
            element={isAuthenticated ? <PermissionsSettingsPage /> : <Navigate to="/login" replace />}
          />
          <Route
            path="/test-mode"
            element={isAuthenticated ? <TestModePage /> : <Navigate to="/login" replace />}
          />
          <Route
            path="/contacts"
            element={isAuthenticated ? gatedRoute('contacts', <ContactsPage />) : <Navigate to="/login" replace />}
          />
          <Route
            path="/photos"
            element={isAuthenticated ? gatedRoute('photos', <PhotosPage />) : <Navigate to="/login" replace />}
          />
          <Route
            path="/info"
            element={isAuthenticated ? gatedRoute('info', <InfoPage />) : <Navigate to="/login" replace />}
          />
          <Route
            path="/voicemail"
            element={isAuthenticated ? gatedRoute('voicemail', <VoicemailPage />) : <Navigate to="/login" replace />}
          />
          <Route
            path="/call-history"
            element={isAuthenticated ? gatedRoute('callHistory', <CallHistoryPage />) : <Navigate to="/login" replace />}
          />
          <Route
            path="/dialer"
            element={isAuthenticated ? gatedRoute('calls', <DialerPage />) : <Navigate to="/login" replace />}
          />
          <Route
            path="/apps"
            element={isAuthenticated ? <AppsPage /> : <Navigate to="/login" replace />}
          />
          <Route
            path="/games"
            element={isAuthenticated ? gatedRoute('games', <GamesPage />) : <Navigate to="/login" replace />}
          />
          <Route
            path="/games/memory"
            element={isAuthenticated ? <MemoryGamePage /> : <Navigate to="/login" replace />}
          />
          <Route
            path="/games/sudoku"
            element={isAuthenticated ? <SudokuPage /> : <Navigate to="/login" replace />}
          />
          <Route
            path="/games/word-search"
            element={isAuthenticated ? <WordSearchPage /> : <Navigate to="/login" replace />}
          />
          <Route
            path="/games/trivia"
            element={isAuthenticated ? <TriviaPage /> : <Navigate to="/login" replace />}
          />
          <Route
            path="/games/2048"
            element={isAuthenticated ? <Game2048Page /> : <Navigate to="/login" replace />}
          />
          <Route
            path="/games/solitaire"
            element={isAuthenticated ? <SolitairePage /> : <Navigate to="/login" replace />}
          />
          <Route
            path="/games/tic-tac-toe"
            element={isAuthenticated ? <TicTacToePage /> : <Navigate to="/login" replace />}
          />
          <Route
            path="/games/hangman"
            element={isAuthenticated ? <HangmanPage /> : <Navigate to="/login" replace />}
          />
          <Route
            path="/games/color-match"
            element={isAuthenticated ? <SimonPage /> : <Navigate to="/login" replace />}
          />
          <Route
            path="/games/chess"
            element={isAuthenticated ? <ChessPage /> : <Navigate to="/login" replace />}
          />
          <Route
            path="/games/checkers"
            element={isAuthenticated ? <CheckersPage /> : <Navigate to="/login" replace />}
          />
          <Route
            path="/games/find-differences"
            element={isAuthenticated ? <FindDifferencesPage /> : <Navigate to="/login" replace />}
          />
          <Route
            path="/games/sliding-puzzle"
            element={isAuthenticated ? <SlidingPuzzlePage /> : <Navigate to="/login" replace />}
          />
          <Route
            path="/agenda"
            element={isAuthenticated ? gatedRoute('agenda', <AgendaPage />) : <Navigate to="/login" replace />}
          />
          {/* Domotics. Gated on the `domotics` feature, which is site-opt-in: a site must
              write an explicit `true` or this resolves false everywhere below it. The gate
              here only hides the route — every endpoint re-enforces it server-side. */}
          <Route
            path="/domotics"
            element={isAuthenticated ? gatedRoute('domotics', <DomoticsPage />) : <Navigate to="/login" replace />}
          />
          {/* De bewonersinbox. Bewust NIET achter een <FeatureGate>: hij toont ook de
              bestaande gebouwbrede mededelingen, die op elke site bestaan. Een lege lijst
              is een geldig scherm; een verborgen route zou de badge op de HomePage naar
              een 404 laten wijzen. */}
          <Route
            path="/notifications"
            element={isAuthenticated ? <NotificationsPage /> : <Navigate to="/login" replace />}
          />
          <Route
            path="/browser"
            element={isAuthenticated ? <InAppBrowserPage /> : <Navigate to="/login" replace />}
          />
          <Route
            path="/today"
            element={isAuthenticated ? <TodayPage /> : <Navigate to="/login" replace />}
          />
          <Route
            path="/weather"
            element={isAuthenticated ? gatedRoute('weather', <WeatherPage />) : <Navigate to="/login" replace />}
          />
          <Route
            path="/news"
            element={isAuthenticated ? gatedRoute('news', <NewsPage />) : <Navigate to="/login" replace />}
          />
        </Routes>
      </Suspense>
      </Shell>
    </BrowserRouter>
    </PermissionGate>
  );
}
